// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, styledSlider, xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
import { A11y, Navigation, Pagination, Scrollbar, Autoplay, SwiperOptions } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

const enum fields {
    title = "title",
    alignment = "alignment",
    displayedImagesCount = "displayedImagesCount",
    autoScroll = "autoScroll",
    images = "images",
}

const enum imageFields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    openInNewTab = "openInNewTab",
    maxImageHeight = "maxImageHeight",
}

interface ImageModel {
    fields: {
        imageUrl: string;
        altText: string;
        imageLink: string;
        openInNewTab: boolean;
        maxImageHeight: number;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.alignment]: "start" | "center" | "end";
        [fields.displayedImagesCount]: number;
        [fields.autoScroll]: boolean;
        [fields.images]: ImageModel[];
    };
}

type Props = OwnProps;

export interface ImageSliderStyles {
    sliderContainer?: InjectableCss;
    imagesContainer?: InjectableCss;
    imageElement?: InjectableCss;
    image?: LazyImageProps;
}

export const imagesStyles: ImageSliderStyles = {
    sliderContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            text-align: center;
            align-items: center;
        `,
    },
    imagesContainer: {
        css: css`
            ${styledSlider()}
            .swiper-container {
                width: 100% !important;
                height: 140px;
            }

            .swiper-slide {
                display: flex;
                justify-content: center;
                padding: 0 11px;
                ${xScreen("lte", "xs")} {
                    padding: 0px 8px;
                }
            }

            && .swiper-button-next-image {
                right: 72px;
                height: 56px;
                width: 56px;
                border-radius: 50%;
                background-color: ${color("common.white")};
            }

            && .swiper-button-prev-image {
                left: 72px;
                height: 56px;
                width: 56px;
                border-radius: 50%;
                background-color: ${color("common.white")};
            }

            .swiper-pagination {
                display: none;
            }

            .swiper-navigation {
                display: none;
            }
            ${xScreen("lte", "md")} {
                && .swiper-button-next,
                .swiper-button-prev {
                    top: unset;
                }

                .swiper-navigation {
                    display: block;
                }

                && .swiper-button-prev-image {
                    left: calc(50vw - 92px);
                    bottom: -24px;
                    background-color: inherit;
                    svg {
                        height: 19px;
                    }
                }

                && .swiper-button-next-image {
                    right: calc(50vw - 92px);
                    bottom: -24px;
                    background-color: inherit;
                    svg {
                        height: 19px;
                    }
                }
                .swiper-pagination {
                    display: block;
                    .swiper-pagination-bullet {
                        margin-right: 4px !important;
                        height: 8px;
                        width: 8px;
                        background-color: inherit;
                        border: 1px solid ${color("common.white")};
                    }
                }
                && .swiper-pagination-bullet-active {
                    background: ${color("common.white")};
                }
                && .swiper-pagination-bullets-dynamic {
                    width: 49px !important;
                    bottom: 0;
                }
            }
            .justify-content-end {
                justify-content: flex-end;
            }
            .justify-content-start {
                justify-content: flex-start;
            }
        `,
    },
    image: {
        css: css`
            && {
                object-fit: contain;
                img {
                    height: auto;
                }
            }
        `,
    },
};

export const swiperOptions: SwiperOptions = {
    spaceBetween: 0,
    wrapperClass: "swiper-container",
    breakpoints: {
        0: {
            slidesPerView: 2,
        },
        700: {
            slidesPerView: 4,
        },
        900: {
            slidesPerView: 4,
        },
        1024: {
            slidesPerView: 4,
        },
        1100: {
            slidesPerView: 4,
        },
    },
    navigation: {
        nextEl: `.swiper-button-next.swiper-button-next-image`,
        prevEl: `.swiper-button-prev.swiper-button-prev-image`,
    },
    pagination: {
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 1000,
    },
};

const MAX_IMAGE_COUNT = 7;

const styles = imagesStyles;

const ImageSlider = ({ fields: { title, alignment, displayedImagesCount, autoScroll, images } }: Props) => {
    useEffect(() => {
        // eslint-disable-next-line global-require
        require("swiper/swiper-bundle.min.css");
    }, []);
    const autoPlay = displayedImagesCount < images.length ? autoScroll : false;

    return (
        <StyledWrapper>
            {title && <Typography>{parse(title, parserOptions)}</Typography>}
            <StyledWrapper
                className="carousel-images-container"
                css={css`
                    ${styles.imagesContainer?.css};
                `}
            >
                <Swiper
                    {...swiperOptions}
                    className="swiper-carousel"
                    modules={[Navigation, Scrollbar, Pagination, A11y, Autoplay]}
                    autoplay={autoPlay}
                    loop={autoPlay}
                    breakpoints={{
                        ...swiperOptions.breakpoints,
                        1300: {
                            slidesPerView: displayedImagesCount || MAX_IMAGE_COUNT,
                        },
                        1700: {
                            slidesPerView: displayedImagesCount
                                ? +displayedImagesCount === 4
                                    ? 4.2
                                    : +displayedImagesCount
                                : MAX_IMAGE_COUNT,
                        },
                        1920: {
                            slidesPerView: displayedImagesCount
                                ? +displayedImagesCount === 4
                                    ? 4.5
                                    : +displayedImagesCount
                                : MAX_IMAGE_COUNT,
                        },
                    }}
                >
                    {images.map(({ fields }, index) => (
                        <SwiperSlide
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className={
                                alignment === "start"
                                    ? "justify-content-start"
                                    : alignment === "end"
                                    ? "justify-content-end"
                                    : ""
                            }
                        >
                            <Link
                                {...styles.sliderContainer}
                                href={fields.imageLink ? useGetLink(fields.imageLink as any).url : ""}
                                target={fields.openInNewTab ? "_blank" : "_self"}
                            >
                                <LazyImage
                                    src={fields.imageUrl}
                                    height={`${fields.maxImageHeight || 100}px`}
                                    altText={fields.altText}
                                    {...styles.image}
                                />
                            </Link>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <StyledWrapper className="swiper-navigation">
                    <div className="swiper-button-prev swiper-button-prev-image">
                        <Icon color="common.white" size={30} src="ChevronLeft" />
                    </div>
                    <div className="swiper-button-next swiper-button-next-image">
                        <Icon color="common.white" size={30} src="ChevronRight" />
                    </div>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ImageSlider,
    definition: {
        group: "XNGAGE" as any,
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.alignment,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "end" },
                ],
                defaultValue: "start",
            },
            {
                name: fields.displayedImagesCount,
                displayName: "Number of images to display",
                editorTemplate: "TextField",
                defaultValue: "7",
                fieldType: "Translatable",
                validate: (value, item) => {
                    if (!value) {
                        return null;
                    }

                    return /^-?\d+$/.test(value) ? null : "please enter a positive number";
                },
            },
            {
                name: fields.autoScroll,
                displayName: "Auto image scroll",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.images,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return `${item.fields?.altText || item.fields?.imageUrl}`;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: imageFields.imageUrl,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: imageFields.altText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: imageFields.imageLink,
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: imageFields.openInNewTab,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: imageFields.maxImageHeight,
                        editorTemplate: "TextField",
                        defaultValue: "100",
                        validate: (value, item) => {
                            if (!value) {
                                return null;
                            }

                            return /^-?\d+$/.test(value) ? null : "please enter a positive number";
                        },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
